import ContentPage from '../components/aoraki/content-page/content-page-container'
import { useEffect, useState } from 'react'
import { APP_NAME, UNAUTHENTICATED_MAIN_MENU_LINKS, FOOTER_MENU_LINKS, FOOTER_LOGOS, APP_LOGO } from '../../constants'
import { AddressOption, GotoControl } from '../components/aoraki/generic-search-maps-react/src'
import { QuickSearch } from '../components/aoraki/generic-search-react/src/components/quick-search'
import RegisterModal from '../components/register-modal'
import SignInModal from '../components/sign-in-modal'
import { determineMessageType, isBannerDataValid, onBannerClose, shouldShowBanner } from '../components/wells/banner'
import { urlPrefix } from '../hooks/use-advanced-search'
import { useGetBannerData } from '../hooks/use-api'
import useLogout from '../hooks/use-logout'
import useRouteQuery from '../hooks/use-route-query'
import useUser from '../hooks/use-user'
import { authButtonHandlerFactory, isValidToken } from '../services/auth-utils'
import { getQuickSearchDisplayOptions } from '../services/form-utils'
import { ContentPageFooter } from '../components/aoraki/content-page/content-page-footer'
import { useCheckUserToken } from '../hooks/use-check-user-token'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Banner,
  Button,
  ButtonsContainer,
  ButtonWithIcon,
  ButtonWithoutIcon,
  CheckableField,
  CheckableFields,
  ContentBanner,
  ContentBlock,
  FeaturesBlock,
  FloatingBlock,
  FormField,
  NzMap,
  PageSkeleton,
  useTitleChange,
} from '@msaf/core-react'

enum SearchBy {
  ADDRESS,
  WELL,
}

export default function LandingPage(...rest: any) {
  useTitleChange(APP_NAME)
  const location = useLocation()
  const navigate = useNavigate()
  const query = useRouteQuery()
  const { logout } = useLogout()
  const { user, isLoading } = useUser()
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false)
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false)
  const [headerActions, setHeaderActions] = useState<Array<ButtonWithIcon | ButtonWithoutIcon>>()
  const [searchBy, setSearchBy] = useState(SearchBy.ADDRESS)
  const { data: bannerData } = useGetBannerData()

  // Open the register or login modal on mount
  useEffect(() => {
    const registerParam = query.get('register')?.toLowerCase()
    const loginParam = query.get('login')?.toLowerCase()
    if (registerParam) {
      setIsRegisterModalOpen(['1', 'true'].includes(registerParam))
    } else if (loginParam) {
      setIsSignInModalOpen(['1', 'true'].includes(loginParam))
    }
  }, [query])

  useEffect(() => {
    let actions: Array<ButtonWithIcon | ButtonWithoutIcon> | undefined = undefined
    if (isValidToken(user?.token)) {
      actions = [
        {
          label: 'Logout',
          onClick: () => logout(),
          icon: 'exit',
          iconAlignment: 'left',
          buttonStyle: 'secondary',
        },
      ]
    } else {
      actions = [
        {
          label: 'Register',
          onClick: authButtonHandlerFactory(location.pathname, setIsRegisterModalOpen),
          buttonStyle: 'primary',
        },
        {
          label: 'Sign in',
          onClick: authButtonHandlerFactory(location.pathname, setIsSignInModalOpen),
          buttonStyle: 'secondary',
        },
      ]
    }
    setHeaderActions(actions)

    // Excluded 'logout' function from dependency list to prevent
    // triggering multiple re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, location.pathname])

  useCheckUserToken()

  if (isLoading) {
    return <PageSkeleton />
  }

  return (
    <>
      {bannerData && isBannerDataValid(bannerData) && (
        <Banner
          message={bannerData.message}
          messageType={determineMessageType(bannerData?.messageType)}
          shouldDisplay={shouldShowBanner(bannerData)}
          onClose={() => onBannerClose(bannerData.expiry)}
        />
      )}
      <ContentPage
        menuLinks={UNAUTHENTICATED_MAIN_MENU_LINKS}
        headerActions={headerActions}
        appLogo={APP_LOGO}
        backgroundColor='blue-wash'
      >
        <SignInModal isOpen={isSignInModalOpen} closeModal={() => setIsSignInModalOpen(false)} />
        <RegisterModal isOpen={isRegisterModalOpen} closeModal={() => setIsRegisterModalOpen(false)} />

        <ContentBanner heading={APP_NAME} blockColor='blue-wash'>
          <p>
            Te Uru Kahika - Regional and Unitary Councils Aotearoa collect and manage large amounts of data on wells,
            geothermal bores and galleries. This data is essential for planning, consenting, compliance and science
            processes. It is also a valuable resource for well owners, drilling companies and consultants. Through the
            Wells Aotearoa New Zealand website, Te Uru Kahika presents data on location, construction, geology and more.
          </p>

          <p>
            Dive in to explore the installations that make New Zealands groundwater and geothermal resources accessible.
          </p>
        </ContentBanner>

        <FloatingBlock heading='Search for a record' backgroundColorAbove='blue-wash'>
          <FormField isSkeleton={isLoading} labelId='searchBy' label='Search by'>
            <CheckableFields ariaLabelledBy='searchBy' isHorizontal>
              <CheckableField
                id='address'
                name='address'
                label='Address'
                isSkeleton={isLoading}
                describedBy='address'
                type='radio'
                checked={searchBy === SearchBy.ADDRESS}
                onChange={() => setSearchBy(SearchBy.ADDRESS)}
              />
              <CheckableField
                id='well'
                name='well'
                label='Well'
                isSkeleton={isLoading}
                describedBy='well'
                type='radio'
                checked={searchBy === SearchBy.WELL}
                onChange={() => setSearchBy(SearchBy.WELL)}
              />
            </CheckableFields>
          </FormField>
          {/* TODO: refactor this into own component if needed elsewhere */}
          <div className='c-public-search'>
            {searchBy === SearchBy.ADDRESS && (
              <GotoControl
                autoFocus={false}
                requestOptions={{ authStrategy: 'none', urlPrefix }}
                onSearch={(selectedAddress: AddressOption) => navigate('/wells/map', { state: { selectedAddress } })}
                labelledBy='goToAddress'
              />
            )}
            {searchBy === SearchBy.WELL && (
              <QuickSearch
                searchTypeKey='search-wells-national'
                placeholderText='Well number or well name'
                onRowClick={(id: string) => navigate(`/wells/${id}/view/summary-details`)}
                getDisplayOptions={getQuickSearchDisplayOptions}
              />
            )}
          </div>
        </FloatingBlock>

        <ContentBlock
          imageAlternative={
            <NzMap
              className='c-content-block__img'
              activeRegions={['Waikato', 'Otago', 'Auckland', 'Greater Wellington', 'Marlborough']}
            />
          }
          heading='A national picture'
        >
          <>
            <p>
              Wells Aotearoa New Zealand aims to make well installation data from across the whole country accessible.
              Regions will come on board over time to create a national dataset. While we work on building the national
              dataset, dive in and explore well installation data within or across regional boundaries that are covered
              so far.
            </p>
            <ButtonsContainer containerStyle='left' verticalSpacing='default'>
              <Button
                path='/wells/map'
                label='Explore wells across the country'
                icon='map-pin-solid'
                iconAlignment='left'
                buttonStyle='secondary'
              />
            </ButtonsContainer>
          </>
        </ContentBlock>

        <FeaturesBlock
          blockColor='blue-wash'
          features={[
            {
              icon: 'search',
              heading: 'Search well records',
              headingLevel: 3,
              bodyText:
                'Search directly using a well number. Or build a flexible search based on a variety of well attributes.',
            },
            {
              icon: 'map',
              heading: 'View well data',
              headingLevel: 3,
              bodyText:
                'Access and view the data Regional and Unitary Councils hold on well installations in their region',
            },
            {
              icon: 'package',
              heading: 'Export data',
              headingLevel: 3,
              bodyText: 'Export well and lithology data into a csv. Or export a summary report for a well record.',
            },
            {
              icon: 'information-circle',
              heading: 'Help and contact us',
              headingLevel: 3,
              bodyText:
                'View help documents to assist your use of the website. Access contact details to report incorrect data.',
            },
          ]}
        />

        <ContentPageFooter appName={APP_NAME} menuLinks={FOOTER_MENU_LINKS} logos={FOOTER_LOGOS} />
      </ContentPage>
    </>
  )
}
